import 'leaflet/dist/leaflet.css';

import { AdminContainer } from '@/components/containers';
import { Loading } from '@/components/loading';
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay';
import { FarmDetailsTabs, FarmToggle, ToggleTabs } from '@/components/toggle/toggle.component';
import { cn } from '@/lib/utils';
import { FarmDetailList } from '@/modules/FarmDetails/FarmDetailList';
import { FarmDetailsMap } from '@/modules/FarmDetails/FarmDetailsMap';
import { farmStatus } from '@/modules/Farms/Farms.constants';
import MarkFarmAsComplete from '@/modules/MarkAsComplete/MarkFarmAsComplete';
import { IfPermitted, UserPermissions } from '@/modules/Permissions';
import { useFarmData, useFarmReportStatus } from '@/modules/Report/Report.api';
import { reportStatus as reportStatusValues } from '@/modules/Report/Report.schema';
import { FarmReport } from '@/pages/FarmReport';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

function FarmDetails() {
  const { projectId, farmId } = useParams<{ customerId: string; projectId: string; farmId: string }>();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as ToggleTabs) || FarmDetailsTabs.LIST;
  const [visibleTab, setVisibleTab] = useState<ToggleTabs>(initialTab);

  const [reportRefetching, setReportRefetching] = useState(false);
  const {
    data: reportData,
    status: reportQueryStatus,
    refetch: reportRefetch,
  } = useFarmReportStatus(farmId, reportRefetching ? 2000 : false);

  const { data: farmData, status: farmQueryStatus, error: farmQueryError } = useFarmData(farmId);

  const isComplete = farmData?.status === farmStatus.COMPLETE;
  const isCsvDownloadable = farmData?.csvDownloadable === true;

  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== visibleTab) {
      setSearchParams({ ...Object.fromEntries(searchParams.entries()), tab: visibleTab }, { replace: true });
    }
    setReportRefetching(visibleTab === FarmDetailsTabs.REPORT && reportData?.status !== reportStatusValues.COMPLETED);
  }, [visibleTab, searchParams, setSearchParams, reportData?.status]);

  useEffect(() => {
    if (reportQueryStatus === 'success' && reportData?.status === reportStatusValues.COMPLETED) {
      setReportRefetching(false);
    }
  }, [reportData, setReportRefetching, reportQueryStatus]);

  const callToAction = useMemo(() => {
    return (
      <div className={cn('flex flex-col gap-4')}>
        {!isComplete && <MarkFarmAsComplete farmId={farmId} isComplete={isComplete} setReportRefetching={setReportRefetching} />}
      </div>
    );
  }, [isComplete, farmId, setReportRefetching]);

  const toggle = <FarmToggle visibleTab={visibleTab} setVisibleTab={setVisibleTab} />;

  if (farmQueryStatus === 'error') {
    throw farmQueryError;
  }

  if (!farmId) {
    toast.error('Farm ID is required');
    return;
  }

  if (!projectId) {
    toast.error('Project ID is required');
    return;
  }

  return (
    <LoadingOverlay isLoading={farmQueryStatus === 'pending'} message="Checking Permissions...">
      <AdminContainer
        className={cn('flex flex-col')}
        title={farmData?.farmInfo.generalInfo.farmName || 'Loading farm...'}
        breadcrumbData={farmData?.breadcrumb}
        callToAction={callToAction}
        searchAndFilter={toggle}
      >
        {farmQueryStatus === 'pending' ? (
          <Loading />
        ) : (
          <IfPermitted
            permissions={[UserPermissions.FARM_DETAIL]}
            conditions={[{ permissions: [UserPermissions.FARM_DETAIL_COMPLETE], condition: () => isComplete }]}
          >
            {visibleTab === FarmDetailsTabs.LIST && <FarmDetailList farmData={farmData} farmId={farmId} />}
            {visibleTab === FarmDetailsTabs.MAP && <FarmDetailsMap farmId={farmId} farmData={farmData} />}
            <div>
              <FarmReport
                visibleTab={visibleTab === FarmDetailsTabs.REPORT}
                projectId={projectId}
                farmId={farmId}
                isCsvDownloadable={isCsvDownloadable}
                data={reportData}
                status={reportQueryStatus}
                refetch={reportRefetch}
                setRefetching={setReportRefetching}
              />
            </div>
          </IfPermitted>
        )}
      </AdminContainer>
    </LoadingOverlay>
  );
}

export default FarmDetails;
